import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useBrand } from '@/contexts/BrandContext';
import { getActiveBrandProducts, Product } from '@/lib/api/services/products';

// Product IDs enum
export enum ProductId {
  MINDCOTINE = 'mindcotine',
  RELIEF = 'mindco-relief', // Using 'mindco-relief' to prevent translation scanner from translating 'relief'
}

// Map ProductId to i18n namespace
export const PRODUCT_I18N_NAMESPACE: Record<ProductId, string> = {
  [ProductId.MINDCOTINE]: 'mindcotine',
  [ProductId.RELIEF]: 'mindco-relief',
};

// Get the i18n namespace for a productId
export function getProductNamespace(productId: ProductId): string {
  return PRODUCT_I18N_NAMESPACE[productId] || 'common';
}

// Storage key for persisting last active product
export const PRODUCT_STORAGE_KEY = 'mindco_last_product';

// Product branding configuration
export interface ProductBranding {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logo: string;
  tagline: string;
  crispWebsiteId: string;
}

// Product features configuration
export interface ProductFeatures {
  programs: boolean;
  tracking: boolean;
  community: boolean;
  assessments: boolean;
  vr360?: boolean;
  meditation?: boolean;
}

// Product configuration interface
export interface ProductConfig {
  id: ProductId;
  name: string;
  slug: string;
  branding: ProductBranding;
  trackingMetrics: string[];
  dashboardWidgets: string[];
  features: ProductFeatures;
  routing: {
    pathPrefix: string;
  };
}

// Context type
export interface ProductContextType {
  currentProduct: ProductId;
  productConfig: ProductConfig;
  setCurrentProduct: (product: ProductId) => void;
  isProduct: (product: ProductId) => boolean;
  switchProduct: (product: ProductId) => void;
}

// Create context
const ProductContext = createContext<ProductContextType | undefined>(undefined);

// Product configurations
const PRODUCT_CONFIGS: Record<ProductId, ProductConfig> = {
  [ProductId.MINDCOTINE]: {
    id: ProductId.MINDCOTINE,
    name: 'MindCotine',
    slug: 'quit-smoking',
    branding: {
      // Green/Teal theme (HSL 171 61% 50%)
      primaryColor: '#20B2AA',
      secondaryColor: '#14A38B',
      accentColor: '#0D8F7A',
      logo: '/mindcotine-logo.svg',
      tagline: 'Your quit smoking journey',
      crispWebsiteId: '162da537-8f73-4d96-9d27-492cabc0ccab',
    },
    trackingMetrics: ['cigarettes', 'cravings', 'mood', 'triggers'],
    dashboardWidgets: ['smokeFreeCounter', 'moneySaved', 'healthImprovements', 'cravingTracker'],
    features: {
      programs: true,
      tracking: true,
      community: true,
      assessments: true,
      vr360: true,
    },
    routing: {
      pathPrefix: '/mindcotine',
    },
  },
  [ProductId.RELIEF]: {
    id: ProductId.RELIEF,
    name: 'MindCo Relief',
    slug: 'reduce-stress',
    branding: {
      // Blue/Cyan theme (HSL 199 89% 48%)
      primaryColor: '#0EA5E9',
      secondaryColor: '#0284C7',
      accentColor: '#0369A1',
      logo: '/relief-logo.svg',
      tagline: 'Your stress management journey',
      crispWebsiteId: '8bd640a5-daea-4f70-af9e-11bf9303dbc0',
    },
    trackingMetrics: ['stress_level', 'anxiety', 'mood', 'sleep_quality'],
    dashboardWidgets: ['stressFreeCounter', 'anxietyTrends', 'mindfulMinutes', 'moodTracker'],
    features: {
      programs: true,
      tracking: true,
      community: true,
      assessments: true,
      meditation: true,
    },
    routing: {
      pathPrefix: '/mindco-relief',
    },
  },
};

// Slug aliases mapping - supports both legacy and canonical paths
export const PRODUCT_SLUG_ALIASES: Record<string, ProductId> = {
  'relief': ProductId.RELIEF,
  'mindco-relief': ProductId.RELIEF,
  'mindcotine': ProductId.MINDCOTINE,
};

// Normalize any product slug variant to the canonical ProductId
export function normalizeProductSlug(slug: string): ProductId | null {
  const normalized = slug.toLowerCase().replace(/^\//, '');
  return PRODUCT_SLUG_ALIASES[normalized] || null;
}

// Get the canonical URL prefix for a product
export function getProductPathPrefix(productId: ProductId): string {
  return PRODUCT_CONFIGS[productId]?.routing.pathPrefix || '/mindcotine';
}

// Get productId from any path (handles all aliases)
export function getProductFromPath(pathname: string): ProductId | null {
  // Check paths in order of specificity (longer paths first)
  if (pathname.startsWith('/mindco-relief') || pathname.startsWith('/relief')) {
    return ProductId.RELIEF;
  }
  if (pathname.startsWith('/mindcotine')) {
    return ProductId.MINDCOTINE;
  }
  return null;
}

// Helper function to detect product from URL - returns null if no product prefix
function detectProductFromPath(pathname: string): ProductId | null {
  return getProductFromPath(pathname);
}

// Helper to get stored product from localStorage
function getStoredProduct(): ProductId | null {
  try {
    const stored = localStorage.getItem(PRODUCT_STORAGE_KEY);
    if (stored && Object.values(ProductId).includes(stored as ProductId)) {
      return stored as ProductId;
    }
  } catch {
    // localStorage not available
  }
  return null;
}

/**
 * Transform database product to ProductConfig format
 */
function transformDbProductToConfig(product: Product): ProductConfig {
  const config = product.config as any || {};
  const branding = product.branding as any || {};

  return {
    id: product.id as ProductId,
    name: product.name,
    slug: product.slug,
    branding: {
      primaryColor: branding?.primaryColor || '#9b87f5',
      secondaryColor: branding?.secondaryColor || '#7E69AB',
      accentColor: branding?.accentColor || '#F97316',
      logo: branding?.logo || '',
      tagline: branding?.tagline || '',
      crispWebsiteId: '', // Database products don't have Crisp by default
    },
    trackingMetrics: config.trackingMetrics || [],
    dashboardWidgets: config.dashboardWidgets || [],
    features: {
      programs: config.features?.programs ?? true,
      tracking: config.features?.tracking ?? true,
      community: config.features?.community ?? false,
      assessments: config.features?.assessments ?? true,
      vr360: config.features?.vr360,
      meditation: config.features?.meditation,
    },
    routing: {
      pathPrefix: `/${product.slug}`,
    },
  };
}

// Helper to validate UUID format
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

// Provider component
export const ProductProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { brand } = useBrand();

  // Only fetch if brand has a valid UUID (not 'default' fallback)
  const hasValidBrandId = brand?.id && isValidUUID(brand.id);

  // Fetch database products for current brand
  const { data: dbProducts, isLoading: _dbProductsLoading } = useQuery({
    queryKey: ['brand-products-active', brand?.id],
    queryFn: async (): Promise<Product[]> => {
      if (!hasValidBrandId) return [];
      const result = await getActiveBrandProducts(brand!.id);
      if (!result.success) {
        console.error('Failed to fetch brand products:', result.error);
        return [];
      }
      return result.data ?? [];
    },
    enabled: !!hasValidBrandId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // Merge hardcoded and database products
  const allProductConfigs = useMemo(() => {
    const merged = { ...PRODUCT_CONFIGS };

    // Add database products
    if (dbProducts) {
      dbProducts.forEach(dbProduct => {
        // Don't override hardcoded products (mindcotine, relief)
        if (!merged[dbProduct.id as ProductId]) {
          merged[dbProduct.id as ProductId] = transformDbProductToConfig(dbProduct);
        }
      });
    }

    return merged;
  }, [dbProducts]);

  const [currentProduct, setCurrentProductState] = useState<ProductId>(() => {
    // Initialize from URL first, then localStorage, then default
    const fromUrl = detectProductFromPath(location.pathname);
    if (fromUrl) return fromUrl;

    const fromStorage = getStoredProduct();
    if (fromStorage) return fromStorage;

    return ProductId.MINDCOTINE;
  });

  // Update product when URL changes - persist to localStorage when detected from URL
  useEffect(() => {
    const detectedProduct = detectProductFromPath(location.pathname);
    if (detectedProduct && detectedProduct !== currentProduct) {
      setCurrentProductState(detectedProduct);
      // Persist to localStorage when navigating to a product route
      try {
        localStorage.setItem(PRODUCT_STORAGE_KEY, detectedProduct);
      } catch {
        // localStorage not available
      }
    }
  }, [location.pathname, currentProduct]);

  // Get product config from merged configs
  const productConfig = allProductConfigs[currentProduct] || PRODUCT_CONFIGS[ProductId.MINDCOTINE];

  const setCurrentProduct = (product: ProductId) => {
    setCurrentProductState(product);
  };

  const isProduct = (product: ProductId): boolean => {
    return currentProduct === product;
  };

  const switchProduct = (product: ProductId) => {
    setCurrentProduct(product);
    // Could add navigation logic here if needed
  };

  const value: ProductContextType = {
    currentProduct,
    productConfig,
    setCurrentProduct,
    isProduct,
    switchProduct,
  };

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

// Custom hook to use the product context
export const useProductContext = (): ProductContextType => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProductContext must be used within a ProductProvider');
  }
  return context;
};

/**
 * Hook to get all available product configurations (hardcoded + database)
 *
 * @example
 * ```tsx
 * const { allProducts, isLoading } = useAllProducts();
 *
 * return (
 *   <div>
 *     {allProducts.map(product => (
 *       <ProductCard key={product.id} product={product} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export const useAllProducts = () => {
  const { brand } = useBrand();

  const { data: dbProducts, isLoading } = useQuery({
    queryKey: ['brand-products-active', brand?.id],
    queryFn: async () => {
      if (!brand?.id) return [];
      const result = await getActiveBrandProducts(brand.id);
      if (!result.success) {
        console.error('Failed to fetch brand products:', result.error);
        return [];
      }
      return result.data;
    },
    enabled: !!brand?.id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  const allProducts = useMemo(() => {
    const products: ProductConfig[] = Object.values(PRODUCT_CONFIGS);

    // Add database products that aren't hardcoded
    if (dbProducts) {
      const hardcodedIds = new Set(Object.keys(PRODUCT_CONFIGS));
      const additionalProducts = dbProducts
        .filter(dbProduct => !hardcodedIds.has(dbProduct.id))
        .map(transformDbProductToConfig);

      products.push(...additionalProducts);
    }

    return products;
  }, [dbProducts]);

  return { allProducts, isLoading };
};

// Export the configurations for use in other parts of the app
export { PRODUCT_CONFIGS };
